import {
  Component,
  OnInit,
  inject,
  signal,
  computed,
  OnDestroy,
  ChangeDetectorRef,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  AfterViewInit,
} from "@angular/core";
import { Router } from "@angular/router";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatRippleModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatFormFieldModule } from "@angular/material/form-field";
import { CommonModule } from "@angular/common";
import { Subject, takeUntil } from "rxjs";
import { MatButton } from "@angular/material/button";
import { NotificationsV2Service } from "./notifications-v2.service";
import { Notification } from "./notifications.types";
import { ng13GlobalSocketV1Service } from "app/core/services/ng13/global-socket-v1.service";
import { AuthDavesaV2Service } from "app/core/auth-davesa/auth-davesa-v2.service";
import { NotificationActionsService, NotificationAction } from "app/core/services/notification-actions.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { getNotificationIcon } from "app/core/enum/consent-status.enum";
import { isOrganizationUser } from "app/core/utils/user-utils";

@Component({
  selector: "notifications",
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatRippleModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatFormFieldModule,
  ],
  templateUrl: "./notifications.component.html",
  styleUrls: ["./notifications.component.scss"],
})
export class NotificationsComponent implements OnInit, AfterViewInit, OnDestroy {
  private _notificationsService = inject(NotificationsV2Service);
  private _router = inject(Router);
  private _overlay = inject(Overlay);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _viewContainerRef = inject(ViewContainerRef);
  private _globalSocketService = inject(ng13GlobalSocketV1Service);
  private _authService = inject(AuthDavesaV2Service);
  private _actionsService = inject(NotificationActionsService);
  private _snackBar = inject(MatSnackBar);

  @ViewChild("notificationsOrigin") private _notificationsOrigin: MatButton;
  @ViewChild("notificationsPanel")
  private _notificationsPanel: TemplateRef<any>;

  // Signals for component state
  notifications = signal<Notification[]>([]);
  categories = signal<any[]>([]);
  isLoading = signal<boolean>(false);
  currentPage = signal<number>(1);
  hasMore = signal<boolean>(true);
  hasNewNotifications = signal<boolean>(false);
  
  // Filter signals
  selectedCategoryId = signal<number>(0); // 0 means all categories
  showReadNotifications = signal<boolean>(false); // false means unread only

  // Action execution state
  isExecutingAction = signal<boolean>(false);
  currentActionId = signal<string | null>(null);
  
  // Error tracking state - key is "notificationOid:actionId"
  failedActions = signal<Map<string, { 
    message: string;
    isOneTimeAction: boolean;
    timestamp: number;
  }>>(new Map());

  // Computed values
  unreadCount = computed(() => {
    // Use total unread count from service (accurate count from database)
    // Falls back to counting loaded notifications if total count not available
    const totalCount = this._notificationsService.totalUnreadCount();
    if (totalCount !== undefined && totalCount !== null) {
      return totalCount;
    }
    // Fallback: count unread in loaded notifications
    return this.notifications().filter((notification) => !notification.has_read)
      .length;
  });

  private _overlayRef: OverlayRef | null = null;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  /** Refresh every 15s to get notifications in realtime when multi-instance: socket does not work across instances; backend needs Redis adapter for Socket.IO to broadcast. */
  private _listNotificationInterval: ReturnType<typeof setInterval> | null = null;
  private static readonly LIST_NOTIFICATION_POLL_INTERVAL_MS = 15000;

  ngOnInit(): void {
    // Skip notifications for organization users - they use master DB without notifications
    const currentUser = this._authService.loginUser();
    if (isOrganizationUser(currentUser)) {
      return;
    }
    
    this.initializeSocketConnection();
    this.loadNotifications(1, false, true);
    this.loadCategories();
    this.subscribeToSocketNotifications();
    this.subscribeToAuthChanges();
    this.startListNotificationPolling();
  }

  ngAfterViewInit(): void {
    this._changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.stopListNotificationPolling();
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Poll list notification API every 5s for realtime updates.
   * Required when running multi-instance: socket does not work across instances;
   * backend should integrate Redis adapter (e.g. @socket.io/redis-adapter) to broadcast.
   */
  private startListNotificationPolling(): void {
    this.stopListNotificationPolling();
    this._listNotificationInterval = setInterval(() => {
      if (isOrganizationUser(this._authService.loginUser())) return;
      if (this.isLoading()) return;
      // Always poll unread to keep badge/unread count accurate
      this.refreshUnreadCountSilently();

      // Reload list only in Unread view; in Read/All, skip list refresh to avoid UI jumping (badge still updates).
      if (!this.showReadNotifications()) {
        this.loadNotifications(1, false, true);
      }
    }, NotificationsComponent.LIST_NOTIFICATION_POLL_INTERVAL_MS);
  }

  private refreshUnreadCountSilently(): void {
    const params = {
      offset: 0,
      limit: 1,
      sort_field: "created_at",
      sort_direction: "D",
      query: "",
      read_type: 0, // Backend contract: 0 = unread
      category_id: 0,
    };
    this._notificationsService.getAllNotifications(params, {
      skipLoading: true,
    });
  }

  private stopListNotificationPolling(): void {
    if (this._listNotificationInterval != null) {
      clearInterval(this._listNotificationInterval);
      this._listNotificationInterval = null;
    }
  }

  /**
   * Subscribe to auth changes to reconnect socket with new user credentials
   */
  private subscribeToAuthChanges(): void {
    let currentUserId = this._authService.loginUser()?.id;
    
    const authCheckInterval = setInterval(() => {
      const newUser = this._authService.loginUser();
      const newUserId = newUser?.id;
      
      if (currentUserId !== newUserId) {
        console.log('User changed, reconnecting socket...', { 
          oldUserId: currentUserId, 
          newUserId: newUserId 
        });
        
        currentUserId = newUserId;
        
        if (newUserId) {
          this.reconnectSocketForNewUser();
        } else {
          this.handleUserLogout();
        }
      }
    }, 2000);
    
    this._unsubscribeAll.subscribe(() => {
      clearInterval(authCheckInterval);
    });
  }

  /**
   * Reconnect socket with new user credentials and reload notifications
   */
  private reconnectSocketForNewUser(): void {
    try {
      if (this._globalSocketService.socket) {
        this._globalSocketService.disconnectSocket();
      }
      
      setTimeout(() => {
        this._globalSocketService.connectSocket();
        this.notifications.set([]);
        this.loadNotifications(1, false, true);
        this.loadCategories();
        this.startListNotificationPolling();
        this._changeDetectorRef.markForCheck();
      }, 500);
      
    } catch (error) {
      console.error('Error reconnecting socket for new user:', error);
    }
  }

  /**
   * Handle user logout - disconnect socket and clear notifications
   */
  private handleUserLogout(): void {
    try {
      this.stopListNotificationPolling();
      if (this._globalSocketService.socket) {
        this._globalSocketService.disconnectSocket();
      }
      
      this.notifications.set([]);
      this._changeDetectorRef.markForCheck();
      
    } catch (error) {
      console.error('Error handling user logout:', error);
    }
  }

  /**
   * Initialize socket connection for real-time notifications
   */
  private initializeSocketConnection(): void {
    try {
      const currentUser = this._authService.loginUser();
      if (!currentUser?.id) {
        console.log('No authenticated user, skipping socket connection');
        return;
      }
      
      if (this._globalSocketService.socket?.connected) {
        this._globalSocketService.disconnectSocket();
      }
      
      this._globalSocketService.connectSocket();
      
    } catch (error) {
      console.error("Error initializing socket connection:", error);
    }
  }

  /**
   * Process notification link - add domain for /signing URLs
   */
  private processNotificationLink(link: string): string {
    if (!link) return link;
    
    // If link starts with /signing, prepend current domain
    if (link.startsWith('/signing')) {
      const currentDomain = window.location.origin;
      const processedLink = `${currentDomain}${link}`;
      return processedLink;
    }
    
    return link;
  }

  /**
   * Subscribe to real-time socket notifications
   */
  private subscribeToSocketNotifications(): void {
    this._globalSocketService.getGlobalNotification$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((socketResponse: any) => {
        try {
          console.log('Received socket notification:', socketResponse);
          
          if (socketResponse) {
            let notificationDataArray = [];

            if (
              socketResponse.message === "Notification received" &&
              socketResponse.data
            ) {
              notificationDataArray = Array.isArray(socketResponse.data)
                ? socketResponse.data
                : [socketResponse.data];
            }
            else if (socketResponse.subtitle || socketResponse.description) {
              notificationDataArray = [socketResponse];
            }
            else if (Array.isArray(socketResponse)) {
              notificationDataArray = socketResponse;
            }

            for (const notificationData of notificationDataArray) {
              if (
                notificationData &&
                (notificationData.subtitle || notificationData.description)
              ) {
                const currentUser = this._authService.loginUser();
                if (
                  notificationData.user_id &&
                  currentUser?.id &&
                  notificationData.user_id !== currentUser.id
                ) {
                  console.log('Skipping notification for different user:', {
                    notificationUserId: notificationData.user_id,
                    currentUserId: currentUser.id,
                    subtitle: notificationData.subtitle
                  });
                  continue;
                }

                const mappedNotification: Notification = {
                  id: notificationData.id,
                  oid: notificationData.oid,
                  subtitle: notificationData.subtitle,
                  description: notificationData.description,
                  link: this.processNotificationLink(notificationData.link),
                  has_read: notificationData.has_read === true,
                  created_at: notificationData.created_at,
                  updated_at: notificationData.updated_at,
                  notification_category: {
                    id: notificationData.category_id,
                    name: notificationData.category_name,
                    code: notificationData.category_code,
                  },
                };

                const existingNotifications = this.notifications();
                const exists = existingNotifications.some(
                  (n) =>
                    (n.id && n.id === mappedNotification.id) ||
                    (n.oid && n.oid === mappedNotification.oid)
                );

                if (!exists) {
                  console.log('Adding new notification:', {
                    id: mappedNotification.id,
                    subtitle: mappedNotification.subtitle,
                    userId: notificationData.user_id,
                    currentUserId: currentUser?.id
                  });
                  
                  // Add new notification and reload to maintain filter consistency
                  this.loadNotifications(1, false, true);
                  
                  this.hasNewNotifications.set(true);
                  setTimeout(() => {
                    this.hasNewNotifications.set(false);
                  }, 3000);

                  this._changeDetectorRef.markForCheck();
                  this._changeDetectorRef.detectChanges();
                }
              }
            }
          }

          if (
            socketResponse &&
            socketResponse.removeNotificationIds &&
            Array.isArray(socketResponse.removeNotificationIds)
          ) {
            // Reload notifications to maintain consistency
            this.loadNotifications(1, false, true);
          }
        } catch (error) {
          console.error("Error processing socket notification:", error);
        }
      });
  }

  /**
   * Load notifications list with current filters.
   * @param silent When true: no loading state, no top progress bar (for 5s polling).
   */
  async loadNotifications(
    page: number = 1,
    append: boolean = false,
    silent: boolean = false
  ): Promise<void> {
    try {
      if (!silent) this.isLoading.set(true);

      const params = {
        offset: append ? (page - 1) * 20 : 0,
        limit: 20,
        sort_field: "created_at",
        sort_direction: "D",
        query: "",
        read_type: this.showReadNotifications() ? 1 : 0, // 0 = unread only, 1 = all
        category_id: this.selectedCategoryId(), // 0 = all categories
      };

      const response = await this._notificationsService.getAllNotifications(
        params,
        silent ? { skipLoading: true } : undefined
      );

      if (response && Array.isArray(response)) {
        const newNotifications = response as Notification[];
        
        const processedNotifications = newNotifications.map(notification => ({
          ...notification,
          link: this.processNotificationLink(notification.link)
        }));

        if (append) {
          this.notifications.update((current) => [
            ...current,
            ...processedNotifications,
          ]);
        } else {
          this.notifications.set(processedNotifications);
        }

        this.currentPage.set(page);
        this.hasMore.set(newNotifications.length === 20);
      }
    } catch (error) {
      if (!silent) console.error("Error loading notifications:", error);
    } finally {
      if (!silent) this.isLoading.set(false);
    }
  }

  /**
   * Load more notifications (pagination)
   */
  async loadMore(): Promise<void> {
    if (this.hasMore() && !this.isLoading()) {
      const nextPage = this.currentPage() + 1;
      await this.loadNotifications(nextPage, true);
    }
  }

  /**
   * Load notification categories
   */
  async loadCategories(): Promise<void> {
    try {
      const params = {
        offset: 0,
        limit: 0,
        sort_field: "created_at",
        sort_direction: "D",
        query: "",
      };
      await this._notificationsService.notificationCategories(params);
      this.categories.set(this._notificationsService.categories() || []);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  }

  /**
   * Toggle notification read/unread status
   */
  async toggleRead(notification: Notification): Promise<void> {
    try {
      const wasRead = notification.has_read;
      const updateData = {
        oid: notification.oid,
        has_read: !notification.has_read,
      };

      await this._notificationsService.updateNotificationItem(updateData);

      // Update local state
      this.notifications.update((notifications) =>
        notifications.map((n) =>
          n.oid === notification.oid ? { ...n, has_read: !n.has_read } : n
        )
      );
      
      // Update total unread count
      if (wasRead) {
        // Was read, now unread - increment
        this._notificationsService.incrementUnreadCount();
      } else {
        // Was unread, now read - decrement
        this._notificationsService.decrementUnreadCount();
      }
    } catch (error) {
      console.error("Error updating read status:", error);
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    try {
      await this._notificationsService.markAllAsReadNotification();
      // Reload notifications to get updated data from server
      await this.loadNotifications();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  }

  /**
   * Filter notifications by category
   */
  async onCategoryChange(categoryId: number): Promise<void> {
    this.selectedCategoryId.set(categoryId);
    this.currentPage.set(1);
    this.hasMore.set(true);
    await this.loadNotifications();
  }

  /**
   * Toggle showing read notifications
   */
  async onToggleReadNotifications(show: boolean): Promise<void> {
    this.showReadNotifications.set(show);
    this.currentPage.set(1);
    this.hasMore.set(true);
    await this.loadNotifications();
  }

  /**
   * Handle notification content click - navigate to link and mark as read if unread
   */
  async onNotificationContentClick(notification: Notification): Promise<void> {
    try {
      // Mark as read first if it's unread
      if (!notification.has_read) {
        const updateData = {
          oid: notification.oid,
          has_read: true,
        };
        await this._notificationsService.updateNotificationItem(updateData);
        
        // Update local state
        this.notifications.update((notifications) =>
          notifications.map((n) =>
            n.oid === notification.oid ? { ...n, has_read: true } : n
          )
        );
        
        // Decrement total unread count
        this._notificationsService.decrementUnreadCount();
      }

      // Only navigate if notification has a link
      if (!notification.link) {
        return; // Do nothing for notifications without links
      }

      this.closePanel();

      // Process the link to handle /signing URLs
      let processedLink = this.processNotificationLink(notification.link);

      // Navigate based on category
      if (notification.notification_category?.code === "econsent-approval") {
        window.open(processedLink, "_blank");
      } else {
        // For other categories with link, navigate to it
        // Handle both full URLs and relative paths
        let link = processedLink;

        // 1) Detect an absolute URL
        const isAbsolute = /^https?:\/\//i.test(link);
        if (isAbsolute) {
          try {
            const url = new URL(link);
            // 1a) External origins -> full page reload
            if (url.origin !== window.location.origin) {
              window.location.href = link;
              return;
            }
            // 1b) Same-origin -> strip origin
            link = url.pathname + url.search + url.hash;
          } catch {
            console.warn('Invalid URL, dropping into full reload:', link);
            window.location.href = link;
            return;
          }
        }
      
        // 2) Ensure a leading slash for absolute routing
        if (!link.startsWith('/') && !link.startsWith('?') && !link.startsWith('#')) {
          link = '/' + link;
        }
      
        // 3) Try to navigate
        const success = await this._router.navigateByUrl(link);
        if (!success) {
          console.error(`Router failed to match route: ${link}`);
        }
      }
    } catch (error) {
      console.error("Error navigating:", error);
    }
  }

  /**
   * Check if notification content should be clickable
   */
  isNotificationClickable(notification: Notification): boolean {
    // Only clickable if unread and has a link
    return !notification.has_read && !!notification.link;
  }

  /**
   * Track by function for ngFor loops
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  /**
   * Format notification time
   */
  formatTime(dateString: string): string {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080)
      return `${Math.floor(diffInMinutes / 1440)}d ago`;

    return date.toLocaleDateString();
  }

  /**
   * Get plain text from HTML content for tooltip
   */
  getPlainText(htmlContent: string): string {
    if (!htmlContent) return "";
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    return tempDiv.textContent || tempDiv.innerText || "";
  }

  /**
   * Check if text is actually truncated by comparing scroll height with client height
   */
  isTextTruncated(element: HTMLElement): boolean {
    if (!element) return false;
    
    try {
      if (element.clientHeight === 0 || element.clientWidth === 0) {
        return false;
      }
      
      const isHeightTruncated = element.scrollHeight > element.clientHeight;
      const isWidthTruncated = element.scrollWidth > element.clientWidth;
      
      return isHeightTruncated || isWidthTruncated;
    } catch (error) {
      console.warn('Error checking text truncation:', error);
      return false;
    }
  }

  /**
   * Format category code to readable format
   * Convert kebab-case to Title Case (e.g., "esource-form" -> "eSource Form")
   */
  formatCategoryCode(code: string): string {
    if (!code) return '';
    return code
      .split('-')
      .map(word => {
        if (word.toLowerCase() === 'esource') return 'eSource';
        if (word.toLowerCase() === 'econsent') return 'eConsent';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  /**
   * Open the notifications panel
   */
  openPanel(): void {
    if (!this._notificationsPanel || !this._notificationsOrigin) {
      return;
    }

    if (!this._overlayRef) {
      this._createOverlay();
    }

    this._overlayRef.attach(
      new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
    );

    this.hasNewNotifications.set(false);
  }

  /**
   * Close the notifications panel
   */
  closePanel(): void {
    this._overlayRef?.detach();
  }

  /**
   * Create the overlay
   */
  private _createOverlay(): void {
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      backdropClass: "fuse-backdrop-on-mobile",
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(
          this._notificationsOrigin._elementRef.nativeElement
        )
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: "start",
            originY: "bottom",
            overlayX: "start",
            overlayY: "top",
          },
          {
            originX: "start",
            originY: "top",
            overlayX: "start",
            overlayY: "bottom",
          },
          {
            originX: "end",
            originY: "bottom",
            overlayX: "end",
            overlayY: "top",
          },
          {
            originX: "end",
            originY: "top",
            overlayX: "end",
            overlayY: "bottom",
          },
        ]),
    });

    this._overlayRef.backdropClick().subscribe(() => {
      this.closePanel();
    });
  }

  /**
   * Get the appropriate icon for a notification based on category and link
   */
  getNotificationIcon(notification: Notification): string {
    return getNotificationIcon(
      notification.notification_category?.code,
      !!notification.link
    );
  }

  /**
   * Get actions for a notification
   * Centralizes ALL filtering logic for action visibility
   */
  getActions(notification: Notification): NotificationAction[] {
    // Get all possible actions from the actions service (pure declaration)
    const availableActions = this._actionsService.getAvailableActions(notification);
    
    // Apply filtering rules centrally:
    
    // 1. Filter out one-time actions if notification is read
    //    Read notifications should not show actionable buttons
    if (notification.has_read) {
      return availableActions.filter(action => !action.isOneTimeAction);
    }
    
    // 2. Filter out one-time actions if they are already completed
    //    Completed actions should not be shown regardless of read status
    const filteredActions = availableActions.filter(action => {
      if (action.isOneTimeAction && notification.action_completed === true) {
        return false; // Hide completed one-time actions
      }
      return true; // Show all other actions
    });
    
    return filteredActions;
  }

  /**
   * Get action label with proper formatting
   */
  getActionLabel(actionId: string): string {
    const labels = {
      'accept': 'Accept',
      'decline': 'Decline',
      'approve': 'Approve',
      'reject': 'Reject',
      'review': 'Review',
      'view': 'View'
    };
    return labels[actionId] || actionId;
  }

  /**
   * Refresh notifications to get updated action_completed status
   */
  public refreshNotifications(): void {
    this.loadNotifications();
    this._changeDetectorRef.detectChanges();
  }

  /**
   * Execute a notification action (inspired by legacy handleNotificationStatus)
   */
  async executeAction(actionId: string, notification: Notification): Promise<void> {
    if (this.isExecutingAction()) {
      return;
    }

    // Get the action to check its click behavior
    const actions = this.getActions(notification);
    const action = actions.find(a => a.id === actionId);
    
    if (!action) {
      return;
    }

    const actionKey = `${notification.oid}:${actionId}`;

    try {
      this.isExecutingAction.set(true);
      this.currentActionId.set(actionId);
      
      // Clear any previous error for this action
      this.clearActionError(notification.oid, actionId);

      const result = await this._actionsService.executeAction(actionId, notification);

      if (result.success) {
        if (result.message) {
          this._snackBar.open(result.message, 'Close', { duration: 3000 });
        }

        // For one-time actions, update the notification object locally and force UI refresh
        if (action.isOneTimeAction) {
          const wasUnread = !notification.has_read;
          
          // Update the notification object locally to reflect the completed state
          notification.action_completed = true;

          // Update the database to mark action as completed and mark as read
          await this._notificationsService.updateNotificationItem({
            oid: notification.oid,
            action_completed: true,
            has_read: true  // Mark as read when one-time action is executed
          });

          // Update local state to reflect read status
          notification.has_read = true;
          
          // Decrement total unread count if it was unread
          if (wasUnread) {
            this._notificationsService.decrementUnreadCount();
          }
          
          // Force change detection to update the button appearance immediately
          this._changeDetectorRef.detectChanges();
        } else if (result.shouldMarkAsRead) {
          // For repeatable actions, only mark as read if explicitly requested
          if (!notification.has_read) {
            await this.toggleRead(notification);
          }
        }
      } else {
        // Handle error with simple UX
        this.handleActionError(notification.oid, actionId, result.message || 'Action failed', action.isOneTimeAction);
        
        // Show error message in snackbar
        this._snackBar.open(result.message || 'Action failed', 'Close', { duration: 5000 });
      }
    } catch (error: any) {
      console.error('Error executing action:', error);
      
      // Handle unexpected errors
      this.handleActionError(notification.oid, actionId, 'An unexpected error occurred. Please try again.', action.isOneTimeAction);
      
      this._snackBar.open('Failed to execute action', 'Close', { duration: 5000 });
    } finally {
      this.isExecutingAction.set(false);
      this.currentActionId.set(null);
    }
  }

  /**
   * Handle action error by storing it in state
   */
  private handleActionError(notificationOid: string, actionId: string, message: string, isOneTimeAction: boolean): void {
    const actionKey = `${notificationOid}:${actionId}`;
    const failedActionsMap = new Map(this.failedActions());
    
    failedActionsMap.set(actionKey, {
      message,
      isOneTimeAction,
      timestamp: Date.now()
    });
    
    this.failedActions.set(failedActionsMap);
  }

  /**
   * Clear action error from state
   */
  clearActionError(notificationOid: string, actionId: string): void {
    const actionKey = `${notificationOid}:${actionId}`;
    const failedActionsMap = new Map(this.failedActions());
    
    if (failedActionsMap.has(actionKey)) {
      failedActionsMap.delete(actionKey);
      this.failedActions.set(failedActionsMap);
    }
  }

  /**
   * Check if an action has failed
   */
  hasActionFailed(notification: Notification, actionId: string): boolean {
    const actionKey = `${notification.oid}:${actionId}`;
    return this.failedActions().has(actionKey);
  }

  /**
   * Get action error info
   */
  getActionError(notification: Notification, actionId: string): { 
    message: string;
    isOneTimeAction: boolean;
    timestamp: number;
  } | null {
    const actionKey = `${notification.oid}:${actionId}`;
    return this.failedActions().get(actionKey) || null;
  }

  /**
   * Mark a failed one-time action as completed
   */
  async markActionAsCompleted(actionId: string, notification: Notification): Promise<void> {
    try {
      const result = await this._actionsService.markActionAsCompleted(notification);
      
      if (result.success) {
        // Clear the error state
        this.clearActionError(notification.oid, actionId);
        
        // Update local notification state
        notification.action_completed = true;
        notification.has_read = true;
        
        // Force change detection
        this._changeDetectorRef.detectChanges();
        
        this._snackBar.open('Action marked as completed', 'Close', { duration: 3000 });
      } else {
        this._snackBar.open(result.message || 'Failed to mark as completed', 'Close', { duration: 5000 });
      }
    } catch (error: any) {
      console.error('Error marking action as completed:', error);
      this._snackBar.open('Failed to mark as completed', 'Close', { duration: 5000 });
    }
  }
}
